import { LandingBackdrop } from "./LandingBackdrop";

/**
 * The ambient backdrop the control screens share: the pre-rendered mosaic wall the
 * old welcome hero commissioned, pushed back far enough to work behind a working
 * surface. The hero is gone — `/` is the screen surface now — and this is the wall's
 * remaining stage.
 *
 * It is a LOOPING VIDEO, not a still — one orientation-picked <video>, muted and
 * autoplaying, with a jpg poster behind it (see LandingBackdrop). What it is not is
 * live: the file ships with the client, so a screen wearing this never touches the
 * catalog API and streams no clips of its own. That is the property the caves
 * overview leaned on — zero media requests until you hover a device — and it is a
 * property of the asset being local, not of it being static.
 *
 * ## Why it is blurred rather than merely dimmed
 *
 * On the old welcome hero this wall WAS the content and stayed bold. Here it sits behind a
 * library the operator is reading, and at plain 30% opacity it still won the fight: the
 * section headings and the quick-setups line were genuinely hard to read over it.
 *
 * Dimming is the intuitive fix and the weak one. What competes with text is not
 * brightness, it is high-frequency detail and sudden motion — a dimmed mosaic still has
 * hard edges and still cuts between clips, and both pull the eye off the words. So the
 * moving layer is attacked on those two axes instead:
 *
 * - **Blur** destroys the edge detail that competes for foveal attention, leaving colour
 *   and movement, which is all "ambient" needs. This is the single biggest effect.
 * - **Half speed** takes out the cuts. Within-clip movement is not what catches
 *   peripheral vision; the jump at a clip boundary is, and halving the rate halves how
 *   often one arrives.
 * - Desaturation and a lower opacity then do what dimming was being asked to do alone.
 *
 * The result still plays and is still plainly visible — it reads as a lit room rather
 * than as a screen behind a screen.
 *
 * `scale-110` is not decorative: a blur samples past its own box and would otherwise
 * pull the transparent edge inward, leaving a soft grey frame around the viewport.
 * Over-scaling puts that artefact outside the clip.
 *
 * The blur sits on this wrapper, never on the <video>. Safe mode blurs media elements
 * with `!important` (see index.css) and the two compose — putting a filter on the
 * element itself would be editing the same property the safe-mode rule owns.
 *
 * Positioned `absolute inset-0` and pointer-events-none, so a caller owes it a
 * positioned, clipped box to fill and has to keep whatever sits above it
 * transparent, or there is nothing to see through.
 */
export function AmbientShowcase() {
  return (
    <div className="pointer-events-none absolute inset-0 transition-opacity duration-700">
      {/* Opacity is deliberately back where it was before this was calmed down. Blur and
          half-speed are what stopped it competing, so there is no reason to also pay for
          it in visibility — dimming it further just made the wall disappear, which is not
          what "ambient" means either. */}
      <div className="absolute inset-0 scale-110 opacity-[.38] blur-[10px] saturate-[.7]">
        <LandingBackdrop playbackRate={0.5} />
      </div>
      {/* Crisp, and outside the blur: this is the layer that keeps the middle of the
          screen — where the cards and the headings are — sitting on darker ground. */}
      <div className="absolute inset-0 bg-[var(--color-ink)]/25" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,var(--color-ink)_92%)]" />
    </div>
  );
}
